import { useCallback, useEffect, useRef, useState } from 'react'

import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { useMutation, useQuery } from '@apollo/client/react'

import { toast, useLocalStorage } from '@buffer-mono/popcorn'
import {
  BufferTrackerReact as BufferTracker,
  Client,
} from '@buffer-mono/tracking-plan'

import { graphql } from '~publish/graphql'
import type { VariablesOf } from '~publish/graphql'
import { useCommonTrackingProperties } from '~publish/hooks/useCommonTrackingProperties'

import { buildInsightsExportSummary } from '../components/ExportDropdown/buildInsightsExportSummary'
import type {
  PostType,
  SortDirection,
  TagComparator,
  TopPostsSortBy,
} from '../graphqlEnums'

const POLL_INTERVAL = 2000
const MAX_POLL_DURATION_MS = 5 * 60 * 1000
// After this duration (~3 polls at the 2s interval) the trigger swaps to a
// more reassuring "it'll download automatically" copy so users with
// longer-running exports know they don't need to wait around.
const LONG_RUNNING_THRESHOLD_MS = 3 * POLL_INTERVAL

// Scoped per organization so that switching orgs in the same browser
// doesn't surface a stale job from another org. The server would reject
// a cross-org jobId via authz, but keeping the key org-scoped means we
// don't even ask.
const jobIdStorageKey = (organizationId: string): string =>
  `insights:export-job-id:${organizationId}`

export const CreateChannelAnalyticsExport = graphql(/* GraphQL */ `
  mutation CreateChannelAnalyticsExport(
    $input: CreateChannelAnalyticsExportInput!
  ) {
    createChannelAnalyticsExport(input: $input) {
      __typename
      ... on ChannelAnalyticsExportProcessing {
        jobId
      }
      ... on ChannelAnalyticsExportSuccess {
        jobId
        downloadUrl
        filename
      }
      ... on InvalidInputError {
        message
      }
    }
  }
`)

/** A PDF report section, as accepted by {@link CreateChannelAnalyticsExport}. */
export type InsightsExportSection = NonNullable<
  VariablesOf<typeof CreateChannelAnalyticsExport>['input']['excludedSections']
>[number]

export const ChannelAnalyticsExportStatus = graphql(/* GraphQL */ `
  query ChannelAnalyticsExportStatus(
    $input: ChannelAnalyticsExportStatusInput!
  ) {
    channelAnalyticsExportStatus(input: $input) {
      __typename
      ... on ChannelAnalyticsExportProcessing {
        jobId
      }
      ... on ChannelAnalyticsExportSuccess {
        jobId
        downloadUrl
        filename
      }
      ... on ChannelAnalyticsExportFailure {
        jobId
        message
      }
    }
  }
`)

// Pass `filename` explicitly: pre-signed S3 URLs are cross-origin, and some
// browsers (Safari/Firefox) ignore the server's `Content-Disposition` header
// for `<a download>` when the attribute is empty. Setting it client-side is
// what guarantees the saved file is named per spec rather than `<sha256>.csv`.
function triggerDownload(url: string, filename: string): void {
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

interface UseChannelAnalyticsExportOptions {
  organizationId: string
  channelIds: string[]
  startDate: string
  endDate: string
  tags?: TagComparator
  /**
   * Post formats to include, narrowing every post-derived section. An empty
   * list means every format. Follower, reach and view sections read
   * channel-level snapshots, so they stay unfiltered.
   */
  postTypes?: readonly PostType[]
  /**
   * Total number of posts matching the current filters (from `useTopPosts`).
   * Captured at click time and rendered in the success toast so the user
   * sees what was exported, anchored to the click moment.
   */
  postCount?: number
  /**
   * Mirrors the All Channels performance table's active sort. Carried through
   * so the exported channel order matches what the user saw. `metric` is a
   * `PostMetricType` value as a string.
   */
  channelSort?: { metric: string; direction: SortDirection }
  /**
   * Mirrors the single-channel page's active Top Posts sort. Carried through
   * so the exported posts order matches what the user saw.
   */
  postSort?: { field: TopPostsSortBy; direction: SortDirection }
  /**
   * Post-metric columns (display order) from the single-channel Top Posts
   * picker — applied to the exported posts tables.
   */
  postColumns?: string[]
  /**
   * Channel-metric columns (display order) from the All Channels performance
   * table's picker — applied to the PDF channel summary table.
   */
  channelColumns?: string[]
}

/**
 * PDF-only customization collected by the export config dialog. Ignored for
 * CSV/Markdown (which pass no config).
 */
export interface InsightsExportConfigInput {
  /** Sections to omit from the PDF (deny-list). Empty means the full report. */
  excludedSections?: InsightsExportSection[]
  /** Persistent Buffer asset URL of a custom logo for the PDF cover. */
  customLogoUrl?: string
  /** Custom report name shown in the PDF header (falls back to the org name). */
  customReportName?: string
}

interface UseChannelAnalyticsExportResult {
  exportData: (
    format: 'csv' | 'markdown' | 'pdf',
    config?: InsightsExportConfigInput,
  ) => void
  isExporting: boolean
  isLongRunning: boolean
}

export function useChannelAnalyticsExport({
  organizationId,
  channelIds,
  startDate,
  endDate,
  tags,
  postTypes,
  postCount,
  channelSort,
  postSort,
  postColumns,
  channelColumns,
}: UseChannelAnalyticsExportOptions): UseChannelAnalyticsExportResult {
  // Persist to localStorage so the UI survives refresh, tab close, or
  // navigation away (e.g. /publish) and back. The worker keeps writing to
  // S3 regardless of what the client does; hydration on mount lets us
  // resume polling and surface the download when the worker is done.
  const [jobId, setJobId] = useLocalStorage<string | null>(
    jobIdStorageKey(organizationId),
    null,
  )
  const [isLongRunning, setIsLongRunning] = useState(false)
  // Captures the filter context at click time so the success toast describes
  // what the user asked for, not what state the component holds when the
  // worker eventually replies. Cleared on success/failure/timeout.
  const pendingSummaryRef = useRef<string | null>(null)
  // Captures the format the user picked at click time so the success branch —
  // which runs after an async polling cycle and only sees the GraphQL union
  // (no `format` field) — can include it on the tracking event. Hydrated
  // jobs from another session leave this null and intentionally skip the
  // event so we don't count a download the user didn't trigger this session.
  const pendingFormatRef = useRef<'csv' | 'markdown' | 'pdf' | null>(null)

  const commonTrackingProperties = useCommonTrackingProperties()

  const [createExport, { loading: mutationLoading }] = useMutation(
    CreateChannelAnalyticsExport,
  )

  const { data: statusData, error: statusError } = useQuery(
    ChannelAnalyticsExportStatus,
    {
      variables: { input: { jobId: jobId ?? '' } },
      skip: !jobId,
      pollInterval: jobId ? POLL_INTERVAL : 0,
      fetchPolicy: 'network-only',
    },
  )

  // Hydrated jobId no longer recognised by the server (24h Mongo TTL expired,
  // NotFoundError, cross-org leak). Drop it silently — the user didn't initiate
  // anything this session, so a toast would be confusing.
  useEffect(() => {
    if (!statusError) return
    setJobId(null)
    pendingSummaryRef.current = null
    pendingFormatRef.current = null
  }, [statusError, setJobId])

  const surfaceSuccess = useCallback((): void => {
    const summary = pendingSummaryRef.current
    const format = pendingFormatRef.current
    pendingSummaryRef.current = null
    pendingFormatRef.current = null
    if (format) {
      BufferTracker.channelInsightsExported({
        ...commonTrackingProperties,
        clientName: Client.PublishWeb,
        format,
        channelIds,
        channelCount: channelIds.length,
        startDate,
        endDate,
        postCount: postCount ?? 0,
        hasTags: Boolean(tags),
        plan: commonTrackingProperties.atEventBillingPlan ?? '',
      })
    }
    toast.success(
      'Export complete',
      summary ? { description: summary } : undefined,
    )
  }, [
    commonTrackingProperties,
    channelIds,
    startDate,
    endDate,
    postCount,
    tags,
  ])

  useEffect(() => {
    if (!statusData || !jobId) return

    const result = statusData.channelAnalyticsExportStatus
    // Drop late in-flight poll responses after the client-side timeout
    // cleared `jobId` or after a different export was kicked off — without
    // this guard a stale Success could fire a phantom download (or a stale
    // Failure a second toast) after the timeout toast already surfaced.
    if (
      (result.__typename === 'ChannelAnalyticsExportSuccess' ||
        result.__typename === 'ChannelAnalyticsExportFailure') &&
      result.jobId !== jobId
    ) {
      return
    }

    switch (result.__typename) {
      case 'ChannelAnalyticsExportSuccess':
        setJobId(null)
        triggerDownload(result.downloadUrl, result.filename)
        surfaceSuccess()
        break
      case 'ChannelAnalyticsExportFailure':
        setJobId(null)
        pendingSummaryRef.current = null
        pendingFormatRef.current = null
        toast.error(result.message)
        break
    }
  }, [statusData, jobId, setJobId, surfaceSuccess])

  useEffect(() => {
    if (!jobId) {
      setIsLongRunning(false)
      return
    }
    const timeoutId = window.setTimeout(
      () => setIsLongRunning(true),
      LONG_RUNNING_THRESHOLD_MS,
    )
    return (): void => window.clearTimeout(timeoutId)
  }, [jobId])

  useEffect(() => {
    if (!jobId) return
    const timeoutId = window.setTimeout(() => {
      setJobId(null)
      pendingSummaryRef.current = null
      pendingFormatRef.current = null
      toast.error(
        `Export is taking longer than expected. Please try again. If the issue persists, contact support with job ID: ${jobId}`,
      )
    }, MAX_POLL_DURATION_MS)
    return (): void => window.clearTimeout(timeoutId)
  }, [jobId, setJobId])

  const exportData = useCallback(
    (
      format: 'csv' | 'markdown' | 'pdf',
      config?: InsightsExportConfigInput,
    ) => {
      pendingSummaryRef.current = buildInsightsExportSummary({
        postCount,
        startDate: new Date(startDate),
        endDate: new Date(endDate),
        channelCount: channelIds.length,
        tags,
        postTypes,
      })
      pendingFormatRef.current = format
      void createExport({
        variables: {
          input: {
            organizationId,
            channelIds,
            startDate,
            endDate,
            tags,
            postTypes: postTypes?.length ? [...postTypes] : undefined,
            format,
            channelSort,
            postSort,
            postColumns,
            channelColumns,
            excludedSections: config?.excludedSections,
            customLogoUrl: config?.customLogoUrl,
            customReportName: config?.customReportName,
          },
        },
        onCompleted: (data) => {
          const result = data.createChannelAnalyticsExport
          switch (result.__typename) {
            case 'ChannelAnalyticsExportSuccess':
              triggerDownload(result.downloadUrl, result.filename)
              surfaceSuccess()
              break
            case 'ChannelAnalyticsExportProcessing':
              setJobId(result.jobId)
              break
            case 'InvalidInputError':
              pendingSummaryRef.current = null
              pendingFormatRef.current = null
              toast.error(result.message)
              break
            default: {
              // Any future addition to the mutation payload union without
              // a matching branch above is a TypeScript error here, so a
              // new variant can't silently fall through.
              const exhaustiveResult: never = result
              pendingSummaryRef.current = null
              pendingFormatRef.current = null
              toast.error('Something went wrong. Please try again.')
              return exhaustiveResult
            }
          }
        },
        onError: (error) => {
          pendingSummaryRef.current = null
          pendingFormatRef.current = null
          const graphQLMessage = CombinedGraphQLErrors.is(error)
            ? error.errors[0]?.message
            : undefined
          toast.error(
            graphQLMessage ?? 'Failed to start export. Please try again.',
          )
        },
      })
    },
    [
      organizationId,
      channelIds,
      startDate,
      endDate,
      tags,
      postTypes,
      postCount,
      channelSort,
      postSort,
      postColumns,
      channelColumns,
      createExport,
      setJobId,
      surfaceSuccess,
    ],
  )

  const isExporting = mutationLoading || jobId !== null

  return { exportData, isExporting, isLongRunning }
}
